import React, { useEffect, useState } from 'react';
import { Plus, Play, Trash2, Edit, Database, MoreVertical, TrendingUp, History } from 'lucide-react';
import { API_URL, Pipeline } from '../../types';
import { useNavigate } from 'react-router-dom';
import CodeViewer from '../../components/CodeViewer';
import { useTheme } from '../../ThemeContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/apiClient';

export default function PipelinesPage() {
  const { theme } = useTheme();
  const [versionTags, setVersionTags] = useState<Record<string, { active?: string; draft?: string }>>({});
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [versionsModalPipeline, setVersionsModalPipeline] = useState<Pipeline | null>(null);
  const [versions, setVersions] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [compareSelection, setCompareSelection] = useState<string[]>([]);
  const [compareResult, setCompareResult] = useState<any | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPipelines();
  }, []);

  const fetchPipelines = async () => {
    try {
      const response = await apiGet('/api/pipelines');
      const data: Pipeline[] = await response.json();
      setPipelines(data);

      // Compute version tags per pipeline (active and draft)
      const entries = await Promise.all(
        data.map(async (p) => {
          try {
            let active: string | undefined = undefined;
            let draft: string | undefined = undefined;

            if (p.status === 'active') {
              active = p.version;
            } else if (p.status === 'draft') {
              draft = p.version;
            }

            if (!active) {
              // Fetch versions to locate active entry when current is draft
              const res = await apiGet(`/api/pipelines/${p.pipeline_id}/versions`);
              if (res.ok) {
                const vers = await res.json();
                const activeRow = vers.find((v: any) => String(v.is_active) === 'true');
                if (activeRow) active = activeRow.version;
              }
            }

            // If current is active, there should be no draft; if current is draft we already set it
            return [p.pipeline_id, { active, draft }] as const;
          } catch {
            return [p.pipeline_id, { active: p.status === 'active' ? p.version : undefined, draft: p.status === 'draft' ? p.version : undefined }] as const;
          }
        })
      );
      const map: Record<string, { active?: string; draft?: string }> = {};
      for (const [id, val] of entries) map[id] = val;
      setVersionTags(map);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    }
  };

  const executePipeline = async (pipelineId: string) => {
    setLoading(true);
    try {
      const response = await apiPost(`/api/pipelines/${pipelineId}/execute`);
      const data = await response.json();
      alert(`Pipeline execution started! Job ID: ${data.job_id}`);
    } catch (error) {
      alert('Failed to execute pipeline');
    } finally {
      setLoading(false);
    }
  };

  const deletePipeline = async (pipelineId: string) => {
    if (!window.confirm('Are you sure you want to delete this pipeline?')) return;
    try {
      await apiDelete(`/api/pipelines/${pipelineId}`);
      fetchPipelines();
    } catch (error) {
      alert('Failed to delete pipeline');
    }
  };

  const openVersionsModal = async (pipeline: Pipeline) => {
    // Navigate to the dedicated Versions page
    navigate(`/pipelines/${pipeline.pipeline_id}/versions`);
  };

  const closeVersionsModal = () => {
    setVersionsModalPipeline(null);
    setVersions([]);
    setCompareSelection([]);
    setCompareResult(null);
  };

  const loadVersions = async (pipelineId: string) => {
    setVersionsLoading(true);
    try {
      const res = await apiGet(`/api/pipelines/${pipelineId}/versions`);
      const data = await res.json();
      setVersions(data);
    } catch (e) {
      alert('Failed to load versions');
    } finally {
      setVersionsLoading(false);
    }
  };

  const restoreVersion = async (version: string) => {
    if (!versionsModalPipeline) return;
    if (!window.confirm(`Restore version v${version}? This will overwrite the current pipeline definition.`)) return;
    try {
      const res = await apiPost(`/api/pipelines/${versionsModalPipeline.pipeline_id}/versions/${version}/restore`);
      if (!res.ok) throw new Error('Restore failed');
      await fetchPipelines();
      await loadVersions(versionsModalPipeline.pipeline_id);
      alert(`Restored to v${version}`);
    } catch (e) {
      alert('Failed to restore version');
    }
  };

  const toggleCompareSelection = (version: string) => {
    setCompareSelection(prev => {
      const exists = prev.includes(version);
      let next = exists ? prev.filter(v => v !== version) : [...prev, version];
      if (next.length > 2) next = next.slice(next.length - 2);
      return next;
    });
  };

  const doCompare = async () => {
    if (!versionsModalPipeline) return;
    if (compareSelection.length !== 2) {
      alert('Select exactly two versions to compare');
      return;
    }
    const [v1, v2] = compareSelection;
    try {
      const [r1, r2] = await Promise.all([
        apiGet(`/api/pipelines/${versionsModalPipeline.pipeline_id}/versions/${v1}`),
        apiGet(`/api/pipelines/${versionsModalPipeline.pipeline_id}/versions/${v2}`)
      ]);
      if (!r1.ok || !r2.ok) throw new Error('Failed to load versions');
      const [left, right] = await Promise.all([r1.json(), r2.json()]);
      setCompareResult({ left, right, v1, v2 });
    } catch (e) {
      alert('Failed to compare versions');
    }
  };

  const promotePipeline = async (pipelineId: string) => {
    if (!window.confirm('Promote this pipeline to Active?')) return;
    try {
      const res = await apiPut(`/api/pipelines/${pipelineId}`, { status: 'active' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Promote failed');
      await fetchPipelines();
      alert('Pipeline promoted to Active');
    } catch (e: any) {
      alert(e?.message || 'Failed to promote pipeline');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: theme.text }}>Pipelines</h2>
          <p style={{ margin: '6px 0 0 0', color: theme.textSecondary }}>Create and manage data transformation pipelines</p>
        </div>
        <button
          onClick={() => navigate('/pipelines/new')}
          style={{
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: `1px solid ${theme.buttonBackground}`,
            borderRadius: '6px',
            padding: '10px 14px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={18} />
          Create Pipeline
        </button>
      </div>

      <div style={{ marginTop: 16, background: theme.cardBackground, borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.08)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: theme.textSecondary }}>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Name</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Status</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Version</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}>Description</th>
              <th style={{ padding: 12, borderBottom: `1px solid ${theme.border}` }}></th>
            </tr>
          </thead>
          <tbody>
            {pipelines.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: 48, textAlign: 'center' }}>
                  <Database size={48} color={theme.textSecondary} style={{ margin: '0 auto 16px' }} />
                  <h3 style={{ color: theme.text, margin: '0 0 8px 0' }}>No pipelines yet</h3>
                  <p style={{ color: theme.textSecondary, margin: 0 }}>Create your first pipeline to get started</p>
                </td>
              </tr>
            ) : (
              pipelines.map((pipeline) => {
                const statusColors: Record<string, string> = {
                  draft: theme.gray,
                  active: theme.success,
                  running: theme.info,
                  completed: theme.success,
                  failed: theme.error,
                  paused: theme.warning
                };
                const activeVersion = (versionTags[pipeline.pipeline_id] || {}).active;
                const draftVersion = (versionTags[pipeline.pipeline_id] || {}).draft;
                
                return (
                  <tr key={pipeline.pipeline_id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={{ padding: 12 }}>
                      <div 
                        onClick={() => navigate(`/pipelines/${pipeline.pipeline_id}`)}
                        style={{ fontWeight: 700, color: theme.text, cursor: 'pointer', textDecoration: 'none' }}
                      >
                        {pipeline.name}
                      </div>
                    </td>
                    <td style={{ padding: 12 }}>
                      <span
                        style={{
                          background: statusColors[pipeline.status] || '#718096',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600
                        }}
                      >
                        {pipeline.status?.toUpperCase?.() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {pipeline.version && (
                          <span
                            style={{
                              background: theme.hoverBackground,
                              color: theme.text,
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600
                            }}
                          >
                            v{pipeline.version}
                          </span>
                        )}
                        {activeVersion && (
                          <span
                            title="Active version"
                            style={{
                              background: theme.successLight,
                              color: theme.successDark,
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 700
                            }}
                          >
                            Active v{activeVersion}
                          </span>
                        )}
                        {draftVersion && (
                          <span
                            title="Draft version"
                            style={{
                              background: theme.warningLight,
                              color: theme.warningDark,
                              padding: '4px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 700
                            }}
                          >
                            Draft v{draftVersion}
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: 12, color: theme.textSecondary }}>
                      {pipeline.description || '-'}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            executePipeline(pipeline.pipeline_id);
                          }}
                          disabled={loading}
                          title="Run pipeline"
                          style={{
                            background: theme.success,
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          <Play size={16} />
                          Run
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/pipelines/${pipeline.pipeline_id}/edit`);
                          }}
                          style={{
                            border: `1px solid ${theme.border}`,
                            background: theme.cardBackground,
                            color: theme.text,
                            padding: '8px 12px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/pipelines/${pipeline.pipeline_id}/versions`);
                          }}
                          style={{
                            border: `1px solid ${theme.border}`,
                            background: theme.cardBackground,
                            color: theme.text,
                            padding: '8px 12px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          Versions
                        </button>
                        {pipeline.status === 'draft' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              promotePipeline(pipeline.pipeline_id);
                            }}
                            style={{
                              border: `1px solid ${theme.successDark}`,
                              background: theme.successLight,
                              color: theme.successDark,
                              padding: '8px 12px',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 14,
                              fontWeight: 600
                            }}
                          >
                            Promote
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePipeline(pipeline.pipeline_id);
                          }}
                          style={{
                            border: `1px solid ${theme.errorDark}`,
                            background: theme.errorLight,
                            color: theme.errorDark,
                            padding: '8px 12px',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {versionsModalPipeline && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 50
          }}
          onClick={closeVersionsModal}
        >
          <div
            style={{ background: theme.cardBackground, borderRadius: 12, width: 800, maxHeight: '80vh', overflow: 'auto', padding: 20, border: `1px solid ${theme.border}` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div>
                <h3 style={{ margin: 0, color: theme.text }}>Versions - {versionsModalPipeline.name}</h3>
                <p style={{ margin: '4px 0 0 0', color: theme.textSecondary, fontSize: 12 }}>Select two versions to compare, or restore a previous version.</p>
              </div>
              <button onClick={closeVersionsModal} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>Close</button>
            </div>

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <strong style={{ color: theme.text }}>Version history</strong>
                  <button onClick={() => loadVersions(versionsModalPipeline.pipeline_id)} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                    Refresh
                  </button>
                </div>
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8 }}>
                  {versionsLoading ? (
                    <div style={{ padding: 16, color: theme.text }}>Loading...</div>
                  ) : versions.length === 0 ? (
                    <div style={{ padding: 16, color: theme.textSecondary }}>No versions found</div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: theme.hoverBackground }}>
                          <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.textSecondary }}>Select</th>
                          <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.textSecondary }}>Version</th>
                          <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.textSecondary }}>Created</th>
                          <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.textSecondary }}>Notes</th>
                          <th style={{ textAlign: 'left', padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.textSecondary }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {versions.map((v: any) => (
                          <tr key={v.version}>
                            <td style={{ padding: 8, borderBottom: `1px solid ${theme.border}` }}>
                              <input
                                type="checkbox"
                                checked={compareSelection.includes(v.version)}
                                onChange={() => toggleCompareSelection(v.version)}
                              />
                            </td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.text }}>v{v.version}</td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.text }}>{new Date(v.created_at).toLocaleString()}</td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${theme.border}`, color: theme.textSecondary }}>{v.change_notes || ''}</td>
                            <td style={{ padding: 8, borderBottom: `1px solid ${theme.border}` }}>
                              <button
                                onClick={() => restoreVersion(v.version)}
                                style={{ background: theme.dangerButton, color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
                              >
                                Restore
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    onClick={doCompare}
                    disabled={compareSelection.length !== 2}
                    style={{ background: theme.buttonBackground, color: theme.buttonText, border: 'none', borderRadius: 8, padding: '8px 12px', opacity: compareSelection.length !== 2 ? 0.6 : 1, cursor: compareSelection.length !== 2 ? 'not-allowed' : 'pointer' }}
                  >
                    Compare selected
                  </button>
                  {compareSelection.length > 0 && (
                    <button onClick={() => { setCompareSelection([]); setCompareResult(null); }} style={{ border: `1px solid ${theme.border}`, background: theme.cardBackground, color: theme.text, borderRadius: 8, padding: '6px 10px', cursor: 'pointer' }}>
                      Clear selection
                    </button>
                  )}
                </div>
              </div>

              <div style={{ flex: 1 }}>
                <strong style={{ color: theme.text }}>Side-by-side compare</strong>
                <div style={{ border: `1px solid ${theme.border}`, borderRadius: 8, minHeight: 200, padding: 12, background: theme.hoverBackground }}>
                  {compareResult ? (
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, color: theme.text }}>v{(compareResult as any).v1}</span>
                        </div>
                        <CodeViewer
                          value={JSON.stringify((compareResult as any).left, null, 2)}
                          language="json"
                          height="360px"
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, color: theme.text }}>v{(compareResult as any).v2}</span>
                        </div>
                        <CodeViewer
                          value={JSON.stringify((compareResult as any).right, null, 2)}
                          language="json"
                          height="360px"
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: theme.textSecondary }}>Select two versions and click Compare to see differences.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function PipelineCard({ pipeline, activeVersion, draftVersion, onExecute, onDelete, onEdit, onOpenVersions, onPromote, loading }: {
  pipeline: Pipeline;
  activeVersion?: string;
  draftVersion?: string;
  onExecute: () => void;
  onDelete: () => void;
  onEdit: () => void;
  onOpenVersions: () => void;
  onPromote: () => void;
  loading: boolean;
}) {
  const { theme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const statusColors: Record<string, string> = {
    draft: theme.gray,
    active: theme.success,
    running: theme.info,
    completed: theme.success,
    failed: theme.error,
    paused: theme.warning
  };

  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClickOutside = () => setDropdownOpen(false);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  return (
    <div
      style={{
        background: theme.cardBackground,
        borderRadius: '8px',
        padding: '16px',
        border: `1px solid ${theme.border}`,
        cursor: 'pointer'
      }}
      onClick={() => navigate(`/pipelines/${pipeline.pipeline_id}`)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <h3 style={{ margin: 0, fontSize: '20px', color: theme.text }}>{pipeline.name}</h3>
            <span
              style={{
                background: statusColors[pipeline.status] || '#718096',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 600
              }}
            >
              {pipeline.status?.toUpperCase?.() || 'UNKNOWN'}
            </span>
            {pipeline.version && (
              <span
                style={{
                  background: theme.hoverBackground,
                  color: theme.text,
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                v{pipeline.version}
              </span>
            )}
            {activeVersion && (
              <span
                title="Active version"
                style={{
                  background: theme.successLight,
                  color: theme.successDark,
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 700
                }}
              >
                Active v{activeVersion}
              </span>
            )}
            {draftVersion && (
              <span
                title="Draft version"
                style={{
                  background: theme.warningLight,
                  color: theme.warningDark,
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 700
                }}
              >
                Draft v{draftVersion}
              </span>
            )}
          </div>
          {pipeline.description && (
            <p style={{ margin: 0, color: theme.textSecondary, fontSize: '14px' }}>{pipeline.description}</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', position: 'relative' }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onExecute();
            }}
            disabled={loading}
            title="Run pipeline"
            style={{
              background: theme.success,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '36px',
              minHeight: '36px'
            }}
          >
            <Play size={18} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDropdownOpen(!dropdownOpen);
            }}
            title="More actions"
            style={{
              background: theme.hoverBackground,
              color: theme.text,
              border: `1px solid ${theme.border}`,
              borderRadius: '8px',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '36px',
              minHeight: '36px'
            }}
          >
            <MoreVertical size={18} />
          </button>
          {dropdownOpen && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: theme.cardBackground,
                border: `1px solid ${theme.border}`,
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: '160px',
                zIndex: 10,
                overflow: 'hidden'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onEdit();
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: theme.text,
                  border: 'none',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBackground}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Edit size={16} />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onOpenVersions();
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: theme.text,
                  border: 'none',
                  padding: '10px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBackground}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <History size={16} />
                Versions
              </button>
              {pipeline.status === 'draft' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(false);
                    onPromote();
                  }}
                  style={{
                    width: '100%',
                    background: 'transparent',
                    color: theme.text,
                    border: 'none',
                    padding: '10px 16px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    fontSize: '14px',
                    textAlign: 'left',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBackground}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <TrendingUp size={16} />
                  Promote
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpen(false);
                  onDelete();
                }}
                style={{
                  width: '100%',
                  background: 'transparent',
                  color: theme.error,
                  border: 'none',
                  borderTop: `1px solid ${theme.border}`,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '14px',
                  textAlign: 'left',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = theme.errorLight}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PipelineModal({ onClose, onSave }: { onClose: () => void; onSave: () => void }) {
  const { theme } = useTheme();
  const detectDriver = (jdbcUrl: string): { driver: string; driver_url: string } | null => {
    const url = (jdbcUrl || '').toLowerCase();
    if (!url.startsWith('jdbc:')) return null;
    if (url.includes('postgresql') || url.includes('postgres')) {
      return { driver: 'org.postgresql.Driver', driver_url: 'https://jdbc.postgresql.org/download/' };
    }
    if (url.includes('mysql')) {
      return { driver: 'com.mysql.cj.jdbc.Driver', driver_url: 'https://dev.mysql.com/downloads/connector/j/' };
    }
    if (url.includes('mariadb')) {
      return { driver: 'org.mariadb.jdbc.Driver', driver_url: 'https://mariadb.com/kb/en/about-mariadb-connector-j/' };
    }
    if (url.includes('sqlserver') || url.includes('mssql')) {
      return { driver: 'com.microsoft.sqlserver.jdbc.SQLServerDriver', driver_url: 'https://learn.microsoft.com/sql/connect/jdbc/download-microsoft-jdbc-driver-for-sql-server' };
    }
    if (url.includes('oracle')) {
      return { driver: 'oracle.jdbc.OracleDriver', driver_url: 'https://www.oracle.com/database/technologies/appdev/jdbc-downloads.html' };
    }
    if (url.includes('snowflake')) {
      return { driver: 'net.snowflake.client.jdbc.SnowflakeDriver', driver_url: 'https://docs.snowflake.com/en/user-guide/jdbc-download' };
    }
    if (url.includes('sqlite')) {
      return { driver: 'org.sqlite.JDBC', driver_url: 'https://github.com/xerial/sqlite-jdbc/releases' };
    }
    if (url.includes('clickhouse')) {
      return { driver: 'com.clickhouse.jdbc.ClickHouseDriver', driver_url: 'https://github.com/ClickHouse/clickhouse-jdbc' };
    }
    if (url.includes('redshift')) {
      return { driver: 'com.amazon.redshift.jdbc.Driver', driver_url: 'https://docs.aws.amazon.com/redshift/latest/mgmt/configure-jdbc-connection.html' };
    }
    return null;
  };
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sourceUrl: 'jdbc:mysql://mysql-source:3306/sales_db',
    sourceDriver: 'com.mysql.cj.jdbc.Driver',
    sourceDriverUrl: '',
    sourceTable: 'sales_data',
    sourceUser: 'sales_user',
    sourcePassword: 'sales_pass',
    targetUrl: 'jdbc:postgresql://postgres-target:5432/data_warehouse',
    targetDriver: 'org.postgresql.Driver',
    targetDriverUrl: '',
    targetTable: 'processed_sales',
    targetUser: 'warehouse_user',
    targetPassword: 'warehouse_pass',
    transformations: [] as any[],
  });

  const sourceSuggestion = detectDriver(formData.sourceUrl);
  const targetSuggestion = detectDriver(formData.targetUrl);

  // Preview states
  const [srcPrev, setSrcPrev] = useState<{ columns: string[]; rows: any[][] } | null>(null);
  const [tgtPrev, setTgtPrev] = useState<{ columns: string[]; rows: any[][] } | null>(null);
  const [srcPrevLoading, setSrcPrevLoading] = useState(false);
  const [tgtPrevLoading, setTgtPrevLoading] = useState(false);
  const [srcPrevError, setSrcPrevError] = useState<string | null>(null);
  const [tgtPrevError, setTgtPrevError] = useState<string | null>(null);

  const previewSource = async () => {
    setSrcPrevLoading(true);
    setSrcPrevError(null);
    setSrcPrev(null);
    try {
      const body = {
        connection: {
          url: formData.sourceUrl,
          driver: formData.sourceDriver,
          driver_url: formData.sourceDriverUrl || undefined,
          table: formData.sourceTable,
          user: formData.sourceUser,
          password: formData.sourcePassword,
        },
        limit: 5,
      };
      const res = await apiPost('/api/preview', body);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to preview source');
      setSrcPrev(data);
    } catch (e: any) {
      setSrcPrevError(e?.message || 'Failed to preview source');
    } finally {
      setSrcPrevLoading(false);
    }
  };

  const previewTarget = async () => {
    setTgtPrevLoading(true);
    setTgtPrevError(null);
    setTgtPrev(null);
    try {
      const body = {
        connection: {
          url: formData.targetUrl,
          driver: formData.targetDriver,
          driver_url: formData.targetDriverUrl || undefined,
          table: formData.targetTable,
          user: formData.targetUser,
          password: formData.targetPassword,
        },
        limit: 5,
      };
      const res = await apiPost('/api/preview', body);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to preview target');
      setTgtPrev(data);
    } catch (e: any) {
      setTgtPrevError(e?.message || 'Failed to preview target');
    } finally {
      setTgtPrevLoading(false);
    }
  };

  const handleSourceUrlChange = (value: string) => {
    const suggestion = detectDriver(value);
    setFormData(prev => ({
      ...prev,
      sourceUrl: value,
      sourceDriver: suggestion?.driver || prev.sourceDriver,
      sourceDriverUrl: suggestion?.driver_url || prev.sourceDriverUrl,
    }));
  };
  const handleTargetUrlChange = (value: string) => {
    const suggestion = detectDriver(value);
    setFormData(prev => ({
      ...prev,
      targetUrl: value,
      targetDriver: suggestion?.driver || prev.targetDriver,
      targetDriverUrl: suggestion?.driver_url || prev.targetDriverUrl,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pipeline = {
      name: formData.name,
      description: formData.description,
      source: {
        type: 'jdbc',
        connection: {
          url: formData.sourceUrl,
          driver: formData.sourceDriver,
          driver_url: formData.sourceDriverUrl,
          table: formData.sourceTable,
          user: formData.sourceUser,
          password: formData.sourcePassword,
        },
      },
      transformations: formData.transformations,
      target: {
        type: 'jdbc',
        connection: {
          url: formData.targetUrl,
          driver: formData.targetDriver,
          driver_url: formData.targetDriverUrl,
          table: formData.targetTable,
          user: formData.targetUser,
          password: formData.targetPassword,
        },
        mode: 'overwrite',
      },
    };

    try {
      await apiPost('/api/pipelines', pipeline);
      onSave();
    } catch (error) {
      alert('Failed to create pipeline');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: theme.cardBackground,
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2 style={{ margin: '0 0 24px 0', color: theme.text }}>Create New Pipeline</h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: theme.text }}>Pipeline Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ width: '100%', padding: '10px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '14px', background: theme.inputBackground, color: theme.text }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: theme.text }}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              style={{ width: '100%', padding: '10px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '14px', resize: 'vertical', background: theme.inputBackground, color: theme.text }}
            />
          </div>

          <h3 style={{ marginBottom: '16px', color: theme.text }}>Source Configuration</h3>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: theme.text }}>JDBC URL *</label>
            <input
              type="text"
              required
              value={formData.sourceUrl}
              onChange={(e) => handleSourceUrlChange(e.target.value)}
              style={{ width: '100%', padding: '8px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '13px', background: theme.inputBackground, color: theme.text }}
            />
            {sourceSuggestion && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: theme.textSecondary }}>
                Detected driver: <code style={{ background: theme.codeBackground, padding: '2px 6px', borderRadius: '4px', color: theme.text }}>{sourceSuggestion.driver}</code> —
                using this for the source. <a href={sourceSuggestion.driver_url} target="_blank" rel="noreferrer" style={{ color: theme.info }}>Download driver</a>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: theme.text }}>Table *</label>
              <input
                type="text"
                required
                value={formData.sourceTable}
                onChange={(e) => setFormData({ ...formData, sourceTable: e.target.value })}
                style={{ width: '100%', padding: '8px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '13px', background: theme.inputBackground, color: theme.text }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: theme.text }}>User *</label>
              <input
                type="text"
                required
                value={formData.sourceUser}
                onChange={(e) => setFormData({ ...formData, sourceUser: e.target.value })}
                style={{ width: '100%', padding: '8px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '13px', background: theme.inputBackground, color: theme.text }}
              />
            </div>
          </div>

          <div style={{ marginTop: '8px' }}>
            <button type="button" onClick={previewSource} style={{ padding: '6px 10px', border: `1px solid ${theme.border}`, borderRadius: '6px', background: theme.cardBackground, color: theme.text, cursor: 'pointer' }}>Preview Source</button>
            <div style={{ marginTop: '8px' }}>
              {srcPrevLoading && <div style={{ color: theme.textSecondary }}>Loading preview…</div>}
              {srcPrevError && <div style={{ background: theme.errorLight, color: theme.errorDark, border: `1px solid ${theme.error}`, padding: 8, borderRadius: 6 }}>{srcPrevError}</div>}
              {srcPrev && srcPrev.columns && srcPrev.columns.length > 0 && (
                <div style={{ overflowX: 'auto', background: theme.tableHeaderBg, border: `1px solid ${theme.tableBorder}`, borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {srcPrev.columns.map((c) => (
                          <th key={c} style={{ textAlign: 'left', padding: 8, fontSize: 12, color: theme.textSecondary, borderBottom: `1px solid ${theme.tableBorder}` }}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {srcPrev.rows.map((r, i) => (
                        <tr key={i}>
                          {r.map((v, j) => (
                            <td key={j} style={{ padding: 8, fontSize: 12, color: theme.text, borderBottom: `1px solid ${theme.tableBorder}` }}>{v === null || v === undefined ? '—' : String(v)}</td>
                          ))}
                        </tr>
                      ))}
                      {srcPrev.rows.length === 0 && (
                        <tr><td colSpan={srcPrev.columns.length} style={{ padding: 8, color: theme.textSecondary }}>No rows</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          
          <h3 style={{ marginBottom: '16px', marginTop: '24px', color: theme.text }}>Target Configuration</h3>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: theme.text }}>JDBC URL *</label>
            <input
              type="text"
              required
              value={formData.targetUrl}
              onChange={(e) => handleTargetUrlChange(e.target.value)}
              style={{ width: '100%', padding: '8px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '13px', background: theme.inputBackground, color: theme.text }}
            />
            {targetSuggestion && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: theme.textSecondary }}>
                Detected driver: <code style={{ background: theme.codeBackground, padding: '2px 6px', borderRadius: '4px', color: theme.text }}>{targetSuggestion.driver}</code> —
                using this for the target. <a href={targetSuggestion.driver_url} target="_blank" rel="noreferrer" style={{ color: theme.info }}>Download driver</a>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: theme.text }}>Table *</label>
              <input
                type="text"
                required
                value={formData.targetTable}
                onChange={(e) => setFormData({ ...formData, targetTable: e.target.value })}
                style={{ width: '100%', padding: '8px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '13px', background: theme.inputBackground, color: theme.text }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: theme.text }}>User *</label>
              <input
                type="text"
                required
                value={formData.targetUser}
                onChange={(e) => setFormData({ ...formData, targetUser: e.target.value })}
                style={{ width: '100%', padding: '8px', border: `1px solid ${theme.inputBorder}`, borderRadius: '6px', fontSize: '13px', background: theme.inputBackground, color: theme.text }}
              />
            </div>
          </div>

          <div style={{ marginTop: '8px' }}>
            <button type="button" onClick={previewTarget} style={{ padding: '6px 10px', border: `1px solid ${theme.border}`, borderRadius: '6px', background: theme.cardBackground, color: theme.text, cursor: 'pointer' }}>Preview Target</button>
            <div style={{ marginTop: '8px' }}>
              {tgtPrevLoading && <div style={{ color: theme.textSecondary }}>Loading preview…</div>}
              {tgtPrevError && <div style={{ background: theme.errorLight, color: theme.errorDark, border: `1px solid ${theme.error}`, padding: 8, borderRadius: 6 }}>{tgtPrevError}</div>}
              {tgtPrev && tgtPrev.columns && tgtPrev.columns.length > 0 && (
                <div style={{ overflowX: 'auto', background: theme.tableHeaderBg, border: `1px solid ${theme.tableBorder}`, borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {tgtPrev.columns.map((c) => (
                          <th key={c} style={{ textAlign: 'left', padding: 8, fontSize: 12, color: theme.textSecondary, borderBottom: `1px solid ${theme.tableBorder}` }}>{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tgtPrev.rows.map((r, i) => (
                        <tr key={i}>
                          {r.map((v, j) => (
                            <td key={j} style={{ padding: 8, fontSize: 12, color: theme.text, borderBottom: `1px solid ${theme.tableBorder}` }}>{v === null || v === undefined ? '—' : String(v)}</td>
                          ))}
                        </tr>
                      ))}
                      {tgtPrev.rows.length === 0 && (
                        <tr><td colSpan={tgtPrev.columns.length} style={{ padding: 8, color: theme.textSecondary }}>No rows</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: '10px 24px', border: `1px solid ${theme.border}`, borderRadius: '8px', background: theme.cardBackground, color: theme.text, cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{ padding: '10px 24px', border: 'none', borderRadius: '8px', background: theme.secondaryButton, color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
            >
              Create Pipeline
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PipelineDetailsModal({ pipeline, onClose }: { pipeline: Pipeline; onClose: () => void }) {
  const { theme } = useTheme();
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: theme.cardBackground,
          borderRadius: '12px',
          padding: '32px',
          maxWidth: '700px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <h2 style={{ margin: '0 0 24px 0', color: theme.text }}>{pipeline.name}</h2>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: theme.textSecondary, marginBottom: '12px' }}>Source</h3>
          <div style={{ background: theme.hoverBackground, padding: '16px', borderRadius: '8px' }}>
            {(() => {
              const src: any = (pipeline as any).source || {};
              const type = src?.type || 'jdbc';
              const conn = src?.connection;
              const opts = src?.options || {};
              if (type === 'jdbc' && conn) {
                return (
                  <>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: theme.text }}><strong>Table:</strong> {conn.table}</p>
                    <p style={{ margin: 0, fontSize: '14px', color: theme.textSecondary }}>{conn.url}</p>
                  </>
                );
              }
              if (type === 'excel_directory') {
                return (
                  <div style={{ fontSize: 13, color: theme.text }}>
                    <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> Excel directory</p>
                    <p style={{ margin: '0 0 4px 0' }}><strong>Path:</strong> {opts.path || '—'}</p>
                    <p style={{ margin: '0 0 4px 0' }}><strong>Pattern:</strong> {opts.pattern || '*.xlsx'}</p>
                    {opts.sheet && <p style={{ margin: '0 0 4px 0' }}><strong>Sheet:</strong> {opts.sheet}</p>}
                    {opts.dataAddress && <p style={{ margin: '0 0 4px 0' }}><strong>Data Address:</strong> {opts.dataAddress}</p>}
                    <p style={{ margin: '0 0 4px 0' }}><strong>Header:</strong> {String(opts.header ?? true)}</p>
                    <p style={{ margin: 0 }}><strong>Infer schema:</strong> {String(opts.inferSchema ?? true)}{opts.recursive !== undefined ? ` | Recursive: ${String(opts.recursive)}` : ''}</p>
                  </div>
                );
              }
              if (type === 'csv_directory') {
                return (
                  <div style={{ fontSize: 13, color: theme.text }}>
                    <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> CSV directory</p>
                    <p style={{ margin: '0 0 4px 0' }}><strong>Path:</strong> {opts.path || '—'}</p>
                    <p style={{ margin: '0 0 4px 0' }}><strong>Pattern:</strong> {opts.pattern || '*.csv'}</p>
                    <p style={{ margin: 0 }}><strong>Header:</strong> {String(opts.header ?? true)} | <strong>Infer schema:</strong> {String(opts.inferSchema ?? true)}</p>
                  </div>
                );
              }
              if (type === 'parquet_directory') {
                return (
                  <div style={{ fontSize: 13, color: theme.text }}>
                    <p style={{ margin: '0 0 6px 0' }}><strong>Type:</strong> Parquet directory</p>
                    <p style={{ margin: '0 0 4px 0' }}><strong>Path:</strong> {opts.path || '—'}</p>
                    <p style={{ margin: 0 }}><strong>Pattern:</strong> {opts.pattern || '*.parquet'}</p>
                  </div>
                );
              }
              return (
                <CodeViewer
                  value={JSON.stringify(src, null, 2)}
                  language="json"
                  height="180px"
                />
              );
            })()}
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: theme.textSecondary, marginBottom: '12px' }}>
            Transformations ({pipeline.transformations.length})
          </h3>
          {pipeline.transformations.map((t, idx) => (
            <div key={t.id} style={{ background: theme.hoverBackground, padding: '12px', borderRadius: '8px', marginBottom: '8px' }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: theme.text }}>
                {idx + 1}. {t.type}
              </p>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: theme.textSecondary, marginBottom: '12px' }}>Target</h3>
          <div style={{ background: theme.hoverBackground, padding: '16px', borderRadius: '8px' }}>
            {(() => {
              const tgt: any = (pipeline as any).target || {};
              const type = tgt?.type || 'jdbc';
              const conn = tgt?.connection;
              if (type === 'jdbc' && conn) {
                return (
                  <>
                    <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: theme.text }}><strong>Table:</strong> {conn.table}</p>
                    <p style={{ margin: 0, fontSize: '14px', color: theme.textSecondary }}>{conn.url}</p>
                  </>
                );
              }
              return (
                <CodeViewer
                  value={JSON.stringify(tgt, null, 2)}
                  language="json"
                  height="180px"
                />
              );
            })()}
          </div>
        </div>

        <button
          onClick={onClose}
          style={{ width: '100%', padding: '12px', border: 'none', borderRadius: '8px', background: theme.secondaryButton, color: 'white', cursor: 'pointer', fontSize: '16px', fontWeight: 600 }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
